import React from "react";
import { useTranslation } from "react-i18next";
import { useConsent } from "@/context/ConsentContext";

/**
 * Brand-on cookie consent banner. Custom-built — no third-party CSS to fight.
 * Localized via existing i18n (keys live in common.json).
 * Persists choice to localStorage["tz_consent"] = "accepted" | "rejected".
 */
export default function CookieConsent() {
  const { open, accept, reject } = useConsent();
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("consent.title")}
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[60] w-[min(94vw,720px)]"
    >
      <div className="rounded-xl bg-tz-espresso text-tz-cream border border-tz-cream/10 shadow-2xl px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-4 backdrop-blur">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-primary mb-1.5">{t("consent.eyebrow")}</div>
          <p className="font-serif text-lg sm:text-xl leading-snug">{t("consent.title")}</p>
          <p className="mt-1.5 text-sm text-tz-cream/75 leading-relaxed">{t("consent.body")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            data-testid="cookie-consent-reject"
            onClick={reject}
            className="inline-flex items-center justify-center rounded-lg border border-tz-cream/25 px-4 py-2.5 text-sm font-medium text-tz-cream/90 hover:bg-white/5 transition-colors"
          >
            {t("consent.reject")}
          </button>
          <button
            type="button"
            data-testid="cookie-consent-accept"
            onClick={accept}
            className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium shadow-interactive hover:bg-primary/90 transition-colors"
          >
            {t("consent.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
